<template>
  <div class="driver-actuals-table-wrap" :class="{ 'driver-actuals-table-wrap--fit': fitWidth }">
    <table class="driver-actuals-table" width="100%" cellpadding="0" cellspacing="0">
      <!-- explicit column definitions so table-layout: fixed distributes the
           day columns evenly instead of honoring per-cell min-widths -->
      <colgroup v-if="fitWidth">
        <col style="width: 120px">
        <col v-for="column in leadingColumns" :key="'col-' + column.key" :style="{ width: column.width || '100px' }">
        <template v-for="day in timelineDays">
          <col v-for="slot in timelineSlots" :key="'col-' + day + '-' + slot.startHour">
        </template>
      </colgroup>
      <thead>
        <tr>
          <th :colspan="1 + leadingColumns.length">&nbsp;</th>
          <th
            v-for="day in timelineDays"
            :key="'hdr-' + day"
            class="driver-actuals-hours-header"
            :colspan="timelineSlotsPerDay"
          >{{ day }}</th>
        </tr>
        <tr>
          <th width="120px">{{ resourceColumnLabel }}</th>
          <th
            v-for="column in leadingColumns"
            :key="'lead-hdr-' + column.key"
            :width="column.width || null"
          >{{ column.label }}</th>
          <template v-for="day in timelineDays">
            <th
              v-for="slot in timelineSlots"
              :key="day + '-' + slot.startHour"
              class="driver-actuals-hour-label"
              :class="slotBoundaryClass(slot)"
            >{{ !fitWidth || slot.startHour % 6 === 0 ? slot.label : '' }}</th>
          </template>
        </tr>
      </thead>
      <tbody ref="timelineBody" @mouseover="handleMouseover" @mouseout="handleMouseout" @click="handleClick">
        <tr
          v-for="row in internalRows"
          :key="row._rowKey"
          :class="{ [continuationRowClass]: row[skipCellField] }"
        >
          <template v-if="!row[skipCellField]">
            <td
              :rowspan="row[rowspanField]"
              :class="[resourceCellClass, { [resourceCellGroupedClass]: row[rowspanField] > 1 }, resourceRowClass ? resourceRowClass(row) : null]"
            ><slot name="resource-cell" :row="row">{{ row[labelField] }}</slot></td>
            <td
              v-for="column in leadingColumns"
              :key="row._rowKey + '-lead-' + column.key"
              :rowspan="row[rowspanField]"
              class="driver-actuals-leading-cell"
              :class="column.tdClass ? column.tdClass(row) : null"
            >
              <slot name="leading-cell" :row="row" :column="column">{{ row[column.key] }}</slot>
            </td>
          </template>
          <template v-for="day in timelineDays">
            <td
              v-for="slot in timelineSlots"
              :key="row._rowKey + '-' + day + '-' + slot.startHour"
              class="driver-actuals-slot-cell"
              :class="slotBoundaryClass(slot)"
            >
              <table class="driver-actuals-slot-inner" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td
                    v-for="(slice, sliceIdx) in slot.slices"
                    :key="sliceIdx"
                    class="driver-actuals-util-slice"
                    :class="[row._cellClassCache[day + '|' + slice.hour + '|' + slice.minutes] || emptyCellClasses, { 'driver-actuals-now-slice': isNowSlice(day, slice) }]"
                    :style="{ '--slice-index-in-day': sliceIndexInDay(slot, sliceIdx) }"
                    :data-row-key="row._rowKey"
                    :data-day="day"
                    :data-hour="slice.hour"
                    :data-minutes="slice.minutes"
                  >&nbsp;</td>
                </tr>
              </table>
            </td>
          </template>
        </tr>
        <tr v-if="!internalRows.length">
          <td :colspan="1 + leadingColumns.length + timelineHoursColspan" class="text-center text-muted py-4">No bookings found for this search.</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import moment from 'moment'
import { getCachedCellSegments } from '@/helpers/actuals_timeline_cache'

export default {
  name: 'driver_actuals_timeline_table',

  props: {
    resourceType: {
      type: String,
      default: 'driver',
      validator: value => value === 'driver' || value === 'vehicle',
    },
    rows: { type: Array, default: () => [] },
    timelineDays: { type: Array, default: () => [] },
    timelineSlots: { type: Array, default: () => [] },
    timelineSlotsPerDay: { type: Number, default: 12 },
    // optional columns between the resource label and the hour grid:
    // [{ key, label, width?, tdClass?(row) }] rendered via the leading-cell slot
    leadingColumns: { type: Array, default: () => [] },
    // optional per-row class for the resource (driver/vehicle) cell itself
    resourceRowClass: { type: Function, default: null },
    // squeeze every day column into the viewport width (no horizontal scroll)
    fitWidth: { type: Boolean, default: false },
    // draw a vertical marker on the slice containing the current time
    showNowLine: { type: Boolean, default: false },
  },

  data() {
    return {
      internalRows: [],
      rowByKey: Object.create(null),
      activeHoverCellKey: null,
      emptyCellClasses: Object.freeze([]),
      nowSlice: null,
      nowTimer: null,
    }
  },

  mounted() {
    this.refreshNowSlice()
    if (this.showNowLine)
      this.nowTimer = setInterval(() => this.refreshNowSlice(), 60000)
  },

  beforeDestroy() {
    if (this.nowTimer) {
      clearInterval(this.nowTimer)
      this.nowTimer = null
    }
  },

  watch: {
    rows: {
      immediate: true,
      handler(rows) {
        this.setRows(rows)
      },
    },
  },

  computed: {
    isVehicle() {
      return this.resourceType === 'vehicle'
    },
    resourceColumnLabel() {
      return this.isVehicle ? 'Vehicle' : 'Driver'
    },
    labelField() {
      return this.isVehicle ? 'vehicle_label' : 'driver_label'
    },
    skipCellField() {
      return this.isVehicle ? 'skip_vehicle_cell' : 'skip_driver_cell'
    },
    rowspanField() {
      return this.isVehicle ? 'vehicle_rowspan' : 'driver_rowspan'
    },
    resourceCellClass() {
      return this.isVehicle ? 'driver-actuals-vehicle-cell' : 'driver-actuals-driver-cell'
    },
    resourceCellGroupedClass() {
      return this.isVehicle ? 'driver-actuals-vehicle-cell--grouped' : 'driver-actuals-driver-cell--grouped'
    },
    continuationRowClass() {
      return this.isVehicle ? 'driver-actuals-vehicle-continuation' : 'driver-actuals-driver-continuation'
    },
    timelineHoursColspan() {
      return this.timelineSlotsPerDay * this.timelineDays.length
    },
  },

  methods: {
    slotBoundaryClass(slot) {
      // fit mode drops the per-slot borders; vertical gridlines come back
      // only at day starts (12am, stronger) and 6am/noon/6pm (lighter)
      if (!this.fitWidth)
        return null
      if (slot.startHour === 0)
        return 'driver-actuals-slot--day-start'
      if (slot.startHour % 6 === 0)
        return 'driver-actuals-slot--minor'
      return null
    },
    refreshNowSlice() {
      if (!this.showNowLine) {
        this.nowSlice = null
        return
      }
      // moment() honors moment.tz.setDefault, so this is the tenant's "now"
      const now = moment()
      this.nowSlice = {
        day: now.format('MM-DD-YYYY'),
        hour: now.hours(),
        minutes: Math.floor(now.minutes() / 15) * 15 + 15,
      }
    },
    isNowSlice(day, slice) {
      return !!(this.nowSlice
        && this.nowSlice.day === day
        && this.nowSlice.hour === slice.hour
        && this.nowSlice.minutes === slice.minutes)
    },
    setRows(rows) {
      const rowByKey = Object.create(null)
      const nextRows = (rows || []).map((row, idx) => {
        const key = [row.row_type, row.driver || row.vehicle, row.booking_id, row.start_datetime, idx].join('|')
        row._rowKey = key
        rowByKey[key] = row
        return row
      })
      this.rowByKey = rowByKey
      this.internalRows = nextRows
      this.activeHoverCellKey = null
    },
    cellCacheKey(rowKey, day, hour, minutes) {
      return `${rowKey}|${day}|${hour}|${minutes}`
    },
    sliceIndexInDay(slot, sliceIdx) {
      return (slot.startHour / 2) * 8 + sliceIdx
    },
    handleMouseover(event) {
      const cell = event.target.closest('td.driver-actuals-util-slice[data-row-key]')
      if (!cell)
        return
      const rowKey = cell.dataset.rowKey
      const row = this.rowByKey[rowKey]
      if (!row)
        return
      const day = cell.dataset.day
      const hour = Number(cell.dataset.hour)
      const minutes = Number(cell.dataset.minutes)
      const cellKey = this.cellCacheKey(rowKey, day, hour, minutes)
      if (cellKey === this.activeHoverCellKey)
        return
      this.activeHoverCellKey = cellKey
      const segments = getCachedCellSegments(row, day, hour, minutes)
      this.$emit('cell-hover', { event, segments, cellKey })
    },
    handleMouseout(event) {
      const tbody = this.$refs.timelineBody
      const related = event.relatedTarget
      if (tbody && related && tbody.contains(related))
        return
      this.activeHoverCellKey = null
      this.$emit('cell-leave')
    },
    // click on a bar surfaces its bookings as a modal (pages listen for
    // cell-click); empty slices stay inert
    handleClick(event) {
      const cell = event.target.closest('td.driver-actuals-util-slice[data-row-key]')
      if (!cell)
        return
      const row = this.rowByKey[cell.dataset.rowKey]
      if (!row)
        return
      const segments = getCachedCellSegments(
        row,
        cell.dataset.day,
        Number(cell.dataset.hour),
        Number(cell.dataset.minutes)
      )
      if (!segments || !segments.length)
        return
      this.$emit('cell-click', {
        event,
        segments,
        cellKey: this.cellCacheKey(cell.dataset.rowKey, cell.dataset.day, Number(cell.dataset.hour), Number(cell.dataset.minutes)),
      })
    },
  },
}
</script>

<style scoped>
.driver-actuals-table-wrap {
  /* Matches driver_actuals_filter_chips.vue status chip colors */
  --driver-actuals-ok-bar: hsla(120, 100%, 75%, 0.78);
  --driver-actuals-ok-bar-hover: hsla(120, 100%, 65%, 0.88);
  --driver-actuals-error-bar: rgba(255, 0, 0, 0.38);
  --driver-actuals-error-bar-hover: rgba(255, 0, 0, 0.52);
  --driver-actuals-planned-bar: hsla(200, 40%, 55%, 0.78);
  --driver-actuals-planned-bar-hover: hsla(200, 40%, 45%, 0.88);
  --driver-actuals-overlap-bar: hsla(35, 100%, 70%, 0.82);
  --driver-actuals-overlap-bar-hover: hsla(35, 100%, 60%, 0.92);
  /* Matches the VSM OOS row color family — service/maintenance events */
  --driver-actuals-service-bar: hsla(357, 78%, 65%, 0.6);
  --driver-actuals-service-bar-hover: hsla(357, 78%, 55%, 0.75);
  --driver-actuals-service-stripe: hsl(357, 68%, 62%);
  --driver-actuals-service-stripe-hover: hsl(357, 68%, 52%);
  /* Opaque stripe colors — semi-transparent values blend in repeating gradients */
  --driver-actuals-ok-stripe: #6fcf6f;
  --driver-actuals-ok-stripe-hover: #5ec05e;
  --driver-actuals-error-stripe: #e57373;
  --driver-actuals-error-stripe-hover: #ef5350;
  --driver-actuals-planned-stripe: hsl(200, 40%, 55%);
  --driver-actuals-planned-stripe-hover: hsl(200, 40%, 45%);
  --driver-actuals-overlap-stripe: #ffb74d;
  --driver-actuals-overlap-stripe-hover: #ffa726;
  /* Fixed px stripes + slice-index phase = same look on 1/3/7-day views */
  --driver-actuals-stripe-width: 12px;
  overflow-x: auto;
  max-height: 70vh;
}
.driver-actuals-table,
.driver-actuals-table th,
.driver-actuals-table td {
  border: 1px solid rgba(0, 0, 0, 0.5);
}
.driver-actuals-table th {
  position: sticky;
  top: 0;
  background-color: #f9f8f8;
  z-index: 1;
}
.driver-actuals-slot-cell {
  padding: 0;
  vertical-align: middle;
}
.driver-actuals-slot-inner {
  border-collapse: collapse;
}
.driver-actuals-slot-inner td {
  border: none;
}
.driver-actuals-util-slice {
  position: relative;
  min-width: 3px;
  height: 26px;
  padding: 0;
  background: transparent;
  vertical-align: middle;
}
/* bar-carrying slices are clickable (cell-click opens the booking modal) */
.driver-actuals-util-slice[class*="driver-actuals-bar--"] {
  cursor: pointer;
}
.driver-actuals-bar--ok::before,
.driver-actuals-bar--overlap::before,
.driver-actuals-bar--overlap-ok::before,
.driver-actuals-bar--overlap-planned::before,
.driver-actuals-bar--overlap-error::before,
.driver-actuals-bar--planned::before,
.driver-actuals-bar--service::before,
.driver-actuals-bar--conflict::before,
.driver-actuals-bar--error::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 17px;
  transform: translateY(-50%);
  pointer-events: none;
}
.driver-actuals-bar--ok::before {
  background: var(--driver-actuals-ok-bar);
}
.driver-actuals-bar--overlap::before {
  background: var(--driver-actuals-overlap-bar);
}
.driver-actuals-bar--planned::before {
  background: var(--driver-actuals-planned-bar);
}
.driver-actuals-bar--error::before {
  background: var(--driver-actuals-error-bar);
}
.driver-actuals-bar--service::before {
  background: var(--driver-actuals-service-bar);
}
.driver-actuals-bar--overlap-ok::before,
.driver-actuals-bar--overlap-planned::before,
.driver-actuals-bar--overlap-error::before,
.driver-actuals-bar--conflict::before {
  background-size: calc(var(--driver-actuals-stripe-width) * 2) 100%;
  background-position: calc(var(--slice-index-in-day, 0) * -100%) 0;
}
/* section where 2+ items overlap on a single-row-per-vehicle view: the two
   things that collide there are a planned booking (blue) and a service
   event (red), striped like the actuals overlap bars */
.driver-actuals-bar--conflict::before {
  background-image: repeating-linear-gradient(
    to right,
    var(--driver-actuals-planned-stripe) 0,
    var(--driver-actuals-planned-stripe) var(--driver-actuals-stripe-width),
    var(--driver-actuals-service-stripe) var(--driver-actuals-stripe-width),
    var(--driver-actuals-service-stripe) calc(var(--driver-actuals-stripe-width) * 2)
  );
}
.driver-actuals-bar--overlap-ok::before {
  background-image: repeating-linear-gradient(
    to right,
    var(--driver-actuals-ok-stripe) 0,
    var(--driver-actuals-ok-stripe) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe) calc(var(--driver-actuals-stripe-width) * 2)
  );
}
.driver-actuals-bar--overlap-planned::before {
  background-image: repeating-linear-gradient(
    to right,
    var(--driver-actuals-planned-stripe) 0,
    var(--driver-actuals-planned-stripe) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe) calc(var(--driver-actuals-stripe-width) * 2)
  );
}
.driver-actuals-bar--overlap-error::before {
  background-image: repeating-linear-gradient(
    to right,
    var(--driver-actuals-error-stripe) 0,
    var(--driver-actuals-error-stripe) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe) calc(var(--driver-actuals-stripe-width) * 2)
  );
}
.driver-actuals-bar--solo::before {
  left: 2px;
  right: 2px;
  border-radius: 6px;
}
.driver-actuals-bar--start::before {
  left: 2px;
  border-radius: 6px 0 0 6px;
}
.driver-actuals-bar--end::before {
  right: 2px;
  border-radius: 0 6px 6px 0;
}
.driver-actuals-bar--mid::before {
  border-radius: 0;
}
.driver-actuals-bar--gap::after {
  content: '';
  position: absolute;
  left: 3px;
  top: 50%;
  height: 17px;
  transform: translateY(-50%);
  width: 2px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 1px;
  pointer-events: none;
}
.driver-actuals-util-slice.driver-actuals-bar--ok:hover::before {
  background: var(--driver-actuals-ok-bar-hover);
}
.driver-actuals-util-slice.driver-actuals-bar--overlap:hover::before {
  background: var(--driver-actuals-overlap-bar-hover);
}
.driver-actuals-util-slice.driver-actuals-bar--overlap-ok:hover::before,
.driver-actuals-util-slice.driver-actuals-bar--overlap-planned:hover::before,
.driver-actuals-util-slice.driver-actuals-bar--overlap-error:hover::before {
  background-size: calc(var(--driver-actuals-stripe-width) * 2) 100%;
  background-position: calc(var(--slice-index-in-day, 0) * -100%) 0;
}
.driver-actuals-util-slice.driver-actuals-bar--overlap-ok:hover::before {
  background-image: repeating-linear-gradient(
    to right,
    var(--driver-actuals-ok-stripe-hover) 0,
    var(--driver-actuals-ok-stripe-hover) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe-hover) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe-hover) calc(var(--driver-actuals-stripe-width) * 2)
  );
}
.driver-actuals-util-slice.driver-actuals-bar--overlap-planned:hover::before {
  background-image: repeating-linear-gradient(
    to right,
    var(--driver-actuals-planned-stripe-hover) 0,
    var(--driver-actuals-planned-stripe-hover) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe-hover) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe-hover) calc(var(--driver-actuals-stripe-width) * 2)
  );
}
.driver-actuals-util-slice.driver-actuals-bar--overlap-error:hover::before {
  background-image: repeating-linear-gradient(
    to right,
    var(--driver-actuals-error-stripe-hover) 0,
    var(--driver-actuals-error-stripe-hover) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe-hover) var(--driver-actuals-stripe-width),
    var(--driver-actuals-overlap-stripe-hover) calc(var(--driver-actuals-stripe-width) * 2)
  );
}
.driver-actuals-util-slice.driver-actuals-bar--planned:hover::before {
  background: var(--driver-actuals-planned-bar-hover);
}
.driver-actuals-util-slice.driver-actuals-bar--error:hover::before {
  background: var(--driver-actuals-error-bar-hover);
}
.driver-actuals-util-slice.driver-actuals-bar--service:hover::before {
  background: var(--driver-actuals-service-bar-hover);
}
.driver-actuals-util-slice.driver-actuals-bar--conflict:hover::before {
  background-size: calc(var(--driver-actuals-stripe-width) * 2) 100%;
  background-position: calc(var(--slice-index-in-day, 0) * -100%) 0;
  background-image: repeating-linear-gradient(
    to right,
    var(--driver-actuals-planned-stripe-hover) 0,
    var(--driver-actuals-planned-stripe-hover) var(--driver-actuals-stripe-width),
    var(--driver-actuals-service-stripe-hover) var(--driver-actuals-stripe-width),
    var(--driver-actuals-service-stripe-hover) calc(var(--driver-actuals-stripe-width) * 2)
  );
}
.driver-actuals-hours-header {
  background-color: #ddd;
  text-align: center;
}
.driver-actuals-hour-label {
  background-color: #ddd;
  font-size: 11px;
  font-weight: bold;
  min-width: 14px;
  padding: 2px 0;
}
.driver-actuals-driver-cell,
.driver-actuals-vehicle-cell {
  font-weight: bold;
  vertical-align: middle;
  white-space: nowrap;
}
.driver-actuals-leading-cell {
  vertical-align: middle;
  text-align: center;
  white-space: nowrap;
  padding: 0 4px;
}
/* vertical current-time marker; border paints outside the bar pseudo-elements
   and box-sizing keeps it from shifting the 15-minute grid */
.driver-actuals-util-slice.driver-actuals-now-slice {
  border-left: 2px solid rgba(229, 57, 53, 0.9);
}
.driver-actuals-driver-cell--grouped,
.driver-actuals-vehicle-cell--grouped {
  vertical-align: middle;
}
.driver-actuals-driver-continuation td,
.driver-actuals-vehicle-continuation td {
  border-top: none;
}
/* fit mode: fixed layout divides day columns evenly across the viewport */
.driver-actuals-table-wrap--fit {
  overflow-x: visible;
}
.driver-actuals-table-wrap--fit .driver-actuals-table {
  width: 100%;
  table-layout: fixed;
}
.driver-actuals-table-wrap--fit .driver-actuals-slot-inner {
  table-layout: fixed;
}
.driver-actuals-table-wrap--fit .driver-actuals-util-slice {
  min-width: 0;
}
.driver-actuals-table-wrap--fit .driver-actuals-hour-label {
  min-width: 0;
  font-size: 9px;
  overflow: hidden;
  white-space: nowrap;
}
/* lighter, sparser time gridlines: day starts plus light 6am/noon/6pm marks */
.driver-actuals-table-wrap--fit .driver-actuals-slot-cell,
.driver-actuals-table-wrap--fit .driver-actuals-hour-label {
  border-left: none;
  border-right: none;
}
.driver-actuals-table-wrap--fit .driver-actuals-slot--minor {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}
.driver-actuals-table-wrap--fit .driver-actuals-slot--day-start {
  border-left: 2px solid rgba(0, 0, 0, 0.55);
}
.driver-actuals-table-wrap--fit .driver-actuals-hours-header {
  overflow: hidden;
  white-space: nowrap;
}
.driver-actuals-table-wrap--fit .driver-actuals-driver-cell,
.driver-actuals-table-wrap--fit .driver-actuals-vehicle-cell,
.driver-actuals-table-wrap--fit .driver-actuals-leading-cell {
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
